import { reactive, onMounted, onUnmounted } from 'vue'

interface DialogState {
  isOpen: boolean
  title: string
  message: string
  time?: number
  primaryButtonText?: string
  secondaryButtonText?: string
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  countDownMessage?: string
}

// Convert seconds to milliseconds
const secondsToMs = (seconds: number): number => seconds * 1000

// Default timeout values
const DEFAULT_ERROR_TIMEOUT = 5 // seconds
const DEFAULT_SUCCESS_TIMEOUT = 3 // seconds

// Create a singleton instance
const state = reactive({
  error: {
    isOpen: false,
    title: '',
    message: '',
    time: secondsToMs(DEFAULT_ERROR_TIMEOUT),
    primaryButtonText: undefined,
    onPrimaryAction: undefined,
    countDownMessage: undefined
  } as DialogState,
  success: {
    isOpen: false,
    title: '',
    message: '',
    time: secondsToMs(DEFAULT_SUCCESS_TIMEOUT),
    primaryButtonText: undefined,
    onPrimaryAction: undefined,
    countDownMessage: undefined
  } as DialogState,
  loading: {
    isOpen: false,
    title: '',
    message: '',
  } as DialogState,
  confirmation: {
    isOpen: false,
    title: '',
    message: '',
    primaryButtonText: 'Confirm',
    secondaryButtonText: 'Cancel',
    onPrimaryAction: undefined,
    onSecondaryAction: undefined
  } as DialogState
})

// Global cleanup function
const cleanup = () => {
  state.error.isOpen = false
  state.success.isOpen = false
  state.loading.isOpen = false
  state.confirmation.isOpen = false
  
  state.error.time = secondsToMs(DEFAULT_ERROR_TIMEOUT)
  state.success.time = secondsToMs(DEFAULT_SUCCESS_TIMEOUT)
}

// Add global event listener for cleanup
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', cleanup)
  window.addEventListener('beforeunload', cleanup)
}

export function useDialog() {
  const error = (
    title: string, 
    message: string, 
    seconds?: number,
    primaryButtonText?: string,
    onPrimaryAction?: () => void,
    countDownMessage?: string
  ) => {
    cleanup() // Clean existing dialogs before showing new one
    state.error = { 
      isOpen: true, 
      title, 
      message, 
      time: secondsToMs(seconds ?? DEFAULT_ERROR_TIMEOUT),
      primaryButtonText,
      onPrimaryAction,
      countDownMessage: countDownMessage || 'Closing in'
    }
  }

  const success = (
    title: string, 
    message: string, 
    seconds?: number,
    primaryButtonText?: string,
    onPrimaryAction?: () => void,
    countDownMessage?: string
  ) => {
    cleanup() // Clean existing dialogs before showing new one
    state.success = { 
      isOpen: true, 
      title, 
      message, 
      time: secondsToMs(seconds ?? DEFAULT_SUCCESS_TIMEOUT),
      primaryButtonText,
      onPrimaryAction,
      countDownMessage: countDownMessage || 'Closing in'
    }
  }

  const loading = (title: string, message: string) => {
    cleanup() // Clean existing dialogs before showing new one
    state.loading = { isOpen: true, title, message }
  }

  const confirmation = (
    title: string, 
    message: string, 
    primaryButtonText: string = 'Confirm',
    secondaryButtonText: string = 'Cancel',
    onPrimaryAction?: () => void,
    onSecondaryAction?: () => void
  ) => {
    cleanup() // Clean existing dialogs before showing new one
    state.confirmation = { 
      isOpen: true, 
      title, 
      message, 
      primaryButtonText,
      secondaryButtonText,
      onPrimaryAction,
      onSecondaryAction
    }
  }

  const close = () => {
    state.error.isOpen = false
    state.success.isOpen = false
    state.loading.isOpen = false
    state.confirmation.isOpen = false

    state.error.time = secondsToMs(DEFAULT_ERROR_TIMEOUT)
    state.success.time = secondsToMs(DEFAULT_SUCCESS_TIMEOUT)
  }

  // Clean up on component mount/unmount
  onMounted(() => {
    cleanup()
  })

  onUnmounted(() => {
    cleanup()
  })

  return {
    error,
    success,
    loading,
    confirmation,
    close,
    state
  }
} 